/* global Excel, CustomFunctions, window, CustomEvent, setTimeout */

import { apiClient } from "@/api/client";
import type { SharedStateKey } from "@/types";
import type { OfficeSelection } from "@/types/api";
import { getAiirErrorMessage } from "@/utils/error-messages";
import { tokenCache } from "@/utils/token-cache";
import { CUSTOM_FUNCTIONS_NAMESPACE } from "@/utils/formula-namespace";
import { parseAskFormulaArguments, type AskFormulaInput } from "./ask-parser";
import { parseOfficeSelection } from "./formula-selection";
import {
  performanceNotFoundMessage,
  splitPerformanceArguments,
  type PerformanceQueryResponse,
} from "./performance-formula";

// @ts-ignore
window.sharedState ||= {};

// A revision also invalidates a pending formula when the user changes away and back to the
// same source, or signs out and another user selects the same organization and report.
let selectionRevision = 0;
window.addEventListener("AiirOfficeSelectionChanged", () => {
  selectionRevision += 1;
});

apiClient.setTokenProvider(() => tokenCache.getAccessToken());

const ASK_REQUEST_CONCURRENCY = 4;
const ASK_NETWORK_RETRY_DELAYS_MS = [750, 1750];

type AskQueueTask<T> = {
  run: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (error: unknown) => void;
};

let activeAskRequests = 0;
const pendingAskRequests: AskQueueTask<any>[] = [];

function enqueueAskRequest<T>(run: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    pendingAskRequests.push({ run, resolve, reject });
    drainAskQueue();
  });
}

function drainAskQueue(): void {
  while (activeAskRequests < ASK_REQUEST_CONCURRENCY && pendingAskRequests.length > 0) {
    const task = pendingAskRequests.shift();

    if (!task) {
      return;
    }

    activeAskRequests += 1;

    void task
      .run()
      .then(task.resolve, task.reject)
      .finally(() => {
        activeAskRequests -= 1;
        drainAskQueue();
      });
  }
}

function isTransientFetchError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  return (
    message.includes("load failed") ||
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("network request failed") ||
    message.includes("network connection was lost") ||
    message.includes("could not connect")
  );
}

async function wait(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

async function retryTransientFetch<T>(run: () => Promise<T>): Promise<T> {
  for (let attempt = 0; attempt <= ASK_NETWORK_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      return await run();
    } catch (error) {
      const delay = ASK_NETWORK_RETRY_DELAYS_MS[attempt];

      if (!delay || !isTransientFetchError(error)) {
        throw error;
      }

      await wait(delay);
    }
  }

  return run();
}

const getSharedState = (key: SharedStateKey) => {
  // @ts-ignore
  const value = window.sharedState[key] as string | undefined;
  return value;
};

type FormulaResult = string | CustomFunctions.Error;

type FormulaRequest = AskFormulaInput & OfficeSelection;
type FormulaKind = "due-diligence" | "performance";
type CheckCurrentInvocation = () => void;

function configurationError(kind: FormulaKind): CustomFunctions.Error {
  return new CustomFunctions.Error(
    CustomFunctions.ErrorCode.notAvailable,
    kind === "due-diligence"
      ? `Select Due diligence, a fund and a Kanban request in the Aiir add-in to use ${CUSTOM_FUNCTIONS_NAMESPACE}.ASK.`
      : `Select Performance, a firm or product, and a reporting period in the Aiir add-in to use ${CUSTOM_FUNCTIONS_NAMESPACE}.PERFORMANCE.`
  );
}

/**
 * Asks Aiir a question.
 * @customfunction ASK
 * @param input Question to ask Aiir. Either a string or a cell reference.
 * @param {any[][][]} [parameters] Optional output format and additional context values or ranges.
 * @param {CustomFunctions.StreamingInvocation<string>} invocation Invocation object.
 */
export function ask(
  input: any,
  parameters: any[][][] = [],
  invocation: CustomFunctions.StreamingInvocation<string>
): void {
  void input;
  void parameters;

  runStreamingFormula(
    Array.prototype.slice.call(arguments) as unknown[],
    invocation,
    "due-diligence",
    generateAskResult
  );
}

/**
 * Answers a question from the product performance or firm AUM report selected in the add-in.
 * @customfunction PERFORMANCE
 * @param input Question or field label, such as "Net IRR". Either a string or a cell reference.
 * @param {any} [quarter] Period within the selected report, such as "Q1 2026", "31 Dec 2025" or a date cell. Leave empty for the selected reporting period.
 * @param {any[][][]} [parameters] Optional output format and context cells such as the fund name.
 * @param {CustomFunctions.StreamingInvocation<string>} invocation Invocation object.
 */
export function performance(
  input: any,
  quarter: any,
  parameters: any[][][] = [],
  invocation: CustomFunctions.StreamingInvocation<string>
): void {
  void input;
  void quarter;
  void parameters;

  runStreamingFormula(
    Array.prototype.slice.call(arguments) as unknown[],
    invocation,
    "performance",
    generatePerformanceResult
  );
}

/**
 * Shared driver of the streaming formulas: finds the invocation among the raw arguments (Excel
 * passes it last, after the repeating parameters), shows "Generating...", and resolves the cell
 * through the request queue unless the formula was cancelled meanwhile.
 */
function runStreamingFormula(
  rawArgs: unknown[],
  invocation: CustomFunctions.StreamingInvocation<string>,
  kind: FormulaKind,
  generate: (
    userArgs: unknown[],
    selection: OfficeSelection,
    checkCurrent: CheckCurrentInvocation
  ) => Promise<FormulaResult>
): void {
  const invocationIndex = rawArgs.findIndex(isStreamingInvocation);
  const activeInvocation =
    invocationIndex >= 0 ? (rawArgs[invocationIndex] as CustomFunctions.StreamingInvocation<string>) : invocation;
  const userArgs = invocationIndex >= 0 ? rawArgs.slice(0, invocationIndex) : rawArgs.slice(0, -1);
  // Capture synchronously, before a fill-down waits in the request queue or authentication.
  const serializedSelection = getSharedState("officeSelection");
  const selection = parseOfficeSelection(serializedSelection);
  const revision = selectionRevision;
  let isCanceled = false;

  activeInvocation.onCanceled = () => {
    isCanceled = true;
  };

  if (!selection || selection.context.requestType !== kind) {
    activeInvocation.setResult(configurationError(kind));
    return;
  }

  activeInvocation.setResult("Generating...");

  const setResult = (value: FormulaResult) => {
    if (!isCanceled) {
      activeInvocation.setResult(value);
    }
  };

  const checkCurrent = () => {
    if (isCanceled) throw new Error("Formula canceled.");
    if (revision !== selectionRevision || serializedSelection !== getSharedState("officeSelection")) {
      throw new CustomFunctions.Error(
        CustomFunctions.ErrorCode.notAvailable,
        "The Aiir selection changed while this formula was running. Recalculate the cell to use the current selection."
      );
    }
  };

  void enqueueAskRequest(async () => {
    checkCurrent();
    return generate(userArgs, selection, checkCurrent);
  })
    .then((result) => {
      checkCurrent();
      setResult(result);
    })
    .catch((error: any) => {
      try {
        checkCurrent();
      } catch (staleError) {
        error = staleError;
      }
      setResult(error instanceof CustomFunctions.Error ? error : getAiirErrorMessage(error, "generate the answer"));
    });
}

function isStreamingInvocation(value: unknown): value is CustomFunctions.StreamingInvocation<string> {
  return Boolean(value && typeof (value as { setResult?: unknown }).setResult === "function");
}

/** Parses the formula arguments and checks sign-in and add-in configuration. */
async function prepareFormulaRequest(
  rawUserArgs: unknown[],
  selection: OfficeSelection,
  checkCurrent: CheckCurrentInvocation
): Promise<{ ok: true; value: FormulaRequest } | { ok: false; error: CustomFunctions.Error }> {
  const parsedInput = parseAskFormulaArguments(rawUserArgs);
  if (!parsedInput.ok) {
    return {
      ok: false,
      error: new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, parsedInput.message),
    };
  }

  let accessToken;

  try {
    accessToken = await tokenCache.getAccessToken();
  } catch (error: any) {
    checkCurrent();
    return {
      ok: false,
      error: new CustomFunctions.Error(CustomFunctions.ErrorCode.notAvailable, getAiirErrorMessage(error, "sign in")),
    };
  }
  checkCurrent();

  if (!accessToken) {
    return {
      ok: false,
      error: new CustomFunctions.Error(
        CustomFunctions.ErrorCode.notAvailable,
        "You must be signed in to use this function."
      ),
    };
  }

  return { ok: true, value: { ...parsedInput.value, ...selection } };
}

function notifyFormulaAnswered(
  detail: { questionId: string } & OfficeSelection & Partial<PerformanceQueryResponse>
): void {
  window.dispatchEvent(new CustomEvent("AiirFormulaAnswered", { detail }));
}

async function generateAskResult(
  rawUserArgs: unknown[],
  selection: OfficeSelection,
  checkCurrent: CheckCurrentInvocation
): Promise<FormulaResult> {
  const request = await prepareFormulaRequest(rawUserArgs, selection, checkCurrent);
  if (!request.ok) {
    return request.error;
  }

  const { question, outputFormat, contextParts, organizationId, context } = request.value;
  if (context.requestType !== "due-diligence") return configurationError("due-diligence");
  const { productId, requestId } = context;

  const data = await retryTransientFetch(() => {
    checkCurrent();
    return apiClient.post<{ id: string; answer: string | null }>(
      `/products/${productId}/requests/${requestId}/queries`,
      { question, contextParts, outputFormat },
      { organizationId }
    );
  });
  checkCurrent();

  notifyFormulaAnswered({ questionId: data.id, ...selection });
  if (!data.answer) {
    return "Aiir could not generate an answer from the available context.";
  }

  return data.answer.replace(/\s\[.*?\]\(.*?\)/g, "");
}

async function generatePerformanceResult(
  rawUserArgs: unknown[],
  selection: OfficeSelection,
  checkCurrent: CheckCurrentInvocation
): Promise<FormulaResult> {
  const { parserArgs, quarter } = splitPerformanceArguments(rawUserArgs);
  const request = await prepareFormulaRequest(parserArgs, selection, checkCurrent);
  if (!request.ok) {
    return request.error;
  }

  const { question, outputFormat, contextParts, organizationId, context } = request.value;
  if (context.requestType !== "performance") return configurationError("performance");

  const data = await retryTransientFetch(() => {
    checkCurrent();
    return apiClient.post<PerformanceQueryResponse>(
      "/performance/queries",
      { question, contextParts, outputFormat, quarter, context },
      { organizationId }
    );
  });
  checkCurrent();

  notifyFormulaAnswered({
    questionId: data.id,
    ...selection,
    question: data.question,
    answer: data.answer,
    sourcePath: data.sourcePath,
    reportCitations: data.reportCitations,
    performance: data.performance,
  });

  return data.answer ?? performanceNotFoundMessage(data.performance);
}

/**
 * Outsource a question to an SME.
 * @customfunction EMAIL
 * @param query Question to outsource to an SME.
 * @param email Email of the SME to outsource the question to.
 * @returns String to write.
 */
export async function email(queryInput: string | Excel.Range, email: string): Promise<string> {
  const serializedSelection = getSharedState("officeSelection");
  const selection = parseOfficeSelection(serializedSelection);
  const revision = selectionRevision;
  const checkCurrent = () => {
    if (revision !== selectionRevision || serializedSelection !== getSharedState("officeSelection")) {
      throw new Error("The Aiir selection changed. Recalculate the cell before sending the email request.");
    }
  };
  let query: string;

  try {
    if (!selection || selection.context.requestType !== "due-diligence") {
      return "Select Due diligence, a fund and a Kanban request in the Aiir add-in to use AIIR.EMAIL.";
    }
    const { organizationId, context: selectedContext } = selection;
    const { productId, requestId } = selectedContext;

    const accessToken = await tokenCache.getAccessToken();
    checkCurrent();

    if (!accessToken) {
      return "You must be signed in to use this function.";
    }

    const context = new Excel.RequestContext();
    const workbook = context.workbook.load("name");
    await context.sync();
    const fileContext = workbook.name;

    if (typeof queryInput === "string") {
      query = queryInput;
    } else if (queryInput instanceof Excel.Range) {
      const range = context.workbook.worksheets.getActiveWorksheet().getRange(queryInput.address);
      range.load("values");
      await context.sync();

      query = range.values[0][0];
    } else {
      const error = new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, "Input must be a string.");
      throw error;
    }

    checkCurrent();
    await apiClient.post(
      `/products/${productId}/requests/${requestId}/emails`,
      { email, query, fileContext },
      { organizationId }
    );

    return `Awaiting answer from ${email}.`;
  } catch (error: any) {
    return getAiirErrorMessage(error, "send the email request");
  }
}

CustomFunctions.associate("ASK", ask);
CustomFunctions.associate("PERFORMANCE", performance);
CustomFunctions.associate("EMAIL", email);